/**
 * Product Compare Section – v4
 * Single scroll container. Labels in a separate non-scrolling overlay.
 * JS positions overlay labels to match the Y offset of spacers inside
 * the scroll container.
 */
(function () {
  'use strict';

  document.querySelectorAll('.product-compare').forEach(initSection);

  function initSection(section) {
    const scrollArea = section.querySelector('[data-pc-scroll]');
    const labelsOverlay = section.querySelector('[data-pc-labels-overlay]');
    const stickyHeader = section.querySelector('[data-pc-sticky]');
    const stickyScroll = section.querySelector('[data-pc-sticky-scroll]');
    const productsRow = section.querySelector('[data-pc-products-row]');
    const dotsContainer = section.querySelector('[data-pc-dots]');
    const arrowLeft = section.querySelector('[data-pc-arrow="left"]');
    const arrowRight = section.querySelector('[data-pc-arrow="right"]');

    if (!scrollArea) return;

    const productCount = parseInt(section.dataset.productCount, 10) || 0;
    if (productCount === 0) return;

    /* ================================================================
       1. POSITION OVERLAY LABELS
       Each overlay label is absolutely positioned at the same Y offset
       as its corresponding spacer inside the scroll container.
       ================================================================ */

    function positionLabels() {
      if (!labelsOverlay) return;

      const spacers = scrollArea.querySelectorAll('[data-pc-spacer-index]');
      const labels = labelsOverlay.querySelectorAll('[data-pc-label-index]');
      const scrollTop = scrollArea.offsetTop; // scroll area's top relative to carousel wrapper

      spacers.forEach((spacer, i) => {
        const label = labels[i];
        if (!label) return;
        // spacer.offsetTop is relative to its offsetParent (the grid/scroll-area)
        label.style.top = (scrollTop + spacer.offsetTop) + 'px';
      });

      // Set overlay height to match scroll area
      labelsOverlay.style.height = scrollArea.offsetHeight + 'px';
    }

    /* ================================================================
       2. SCROLL SYNC — main scroll ↔ sticky header
       ================================================================ */

    let activeScroller = null;

    function syncScroll(source, target) {
      if (!target) return;
      if (activeScroller && activeScroller !== source) return;
      activeScroller = source;
      target.scrollLeft = source.scrollLeft;
      requestAnimationFrame(() => { activeScroller = null; });
    }

    scrollArea.addEventListener('scroll', () => {
      syncScroll(scrollArea, stickyScroll);
      updateArrows();
      updateDots();
    }, { passive: true });

    if (stickyScroll) {
      stickyScroll.addEventListener('scroll', () => {
        syncScroll(stickyScroll, scrollArea);
      }, { passive: true });
    }

    /* ================================================================
       3. DETECT SITE STICKY HEADER
       ================================================================ */

    function findSiteHeader() {
      const candidates = [
        '#shopify-section-header',
        '.shopify-section-header',
        'header[data-section-type="header"]',
        '.header-section',
        '.header-wrapper',
        '.site-header',
        '#header',
        'header.header',
        'header'
      ];

      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (el) {
          const style = window.getComputedStyle(el);
          if (
            style.position === 'fixed' ||
            style.position === 'sticky' ||
            el.classList.contains('is-sticky') ||
            el.classList.contains('header--sticky') ||
            el.classList.contains('sticky') ||
            el.dataset.sticky === 'true'
          ) {
            return el;
          }
        }
      }

      const allEls = document.querySelectorAll(
        'header, [class*="header"], [id*="header"], nav'
      );
      for (const el of allEls) {
        const style = window.getComputedStyle(el);
        if (
          (style.position === 'fixed' || style.position === 'sticky') &&
          parseInt(style.top, 10) <= 10 &&
          el.offsetHeight > 0 &&
          el.offsetHeight < 200
        ) {
          return el;
        }
      }

      return null;
    }

    function getSiteHeaderOffset() {
      const header = findSiteHeader();
      if (!header) return 0;
      const rect = header.getBoundingClientRect();
      if (rect.bottom <= 0) return 0;
      return Math.max(0, rect.bottom);
    }

    /* ================================================================
       4. STICKY HEADER LOGIC
       ================================================================ */

    let stickyActive = false;

    function updateSticky() {
      if (!productsRow || !stickyHeader) return;

      const rowRect = productsRow.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const offset = getSiteHeaderOffset();

      const shouldShow =
        rowRect.bottom < offset && sectionRect.bottom > offset + 80;

      if (shouldShow && !stickyActive) {
        stickyActive = true;
        stickyHeader.classList.add('product-compare__sticky-header--visible');
        stickyHeader.setAttribute('aria-hidden', 'false');
        stickyHeader.style.setProperty('--pc-sticky-offset', offset + 'px');
        if (stickyScroll) stickyScroll.scrollLeft = scrollArea.scrollLeft;
      } else if (shouldShow && stickyActive) {
        stickyHeader.style.setProperty('--pc-sticky-offset', offset + 'px');
      } else if (!shouldShow && stickyActive) {
        stickyActive = false;
        stickyHeader.classList.remove('product-compare__sticky-header--visible');
        stickyHeader.setAttribute('aria-hidden', 'true');
      }
    }

    /* ================================================================
       5. CAROUSEL ARROWS
       ================================================================ */

    function positionArrows() {
      if (!arrowLeft && !arrowRight) return;

      const wrapperRect = section.querySelector('.product-compare__carousel-wrapper').getBoundingClientRect();
      const viewportH = window.innerHeight;

      const visibleTop = Math.max(wrapperRect.top, 0);
      const visibleBottom = Math.min(wrapperRect.bottom, viewportH);

      if (visibleBottom <= visibleTop) {
        if (arrowLeft) arrowLeft.classList.remove('product-compare__arrow--visible');
        if (arrowRight) arrowRight.classList.remove('product-compare__arrow--visible');
        return;
      }

      const centerY = (visibleTop + visibleBottom) / 2;

      if (arrowLeft) {
        arrowLeft.style.top = centerY + 'px';
        arrowLeft.style.left = (wrapperRect.left - 8) + 'px';
        arrowLeft.style.transform = 'translateY(-50%)';
      }

      if (arrowRight) {
        arrowRight.style.top = centerY + 'px';
        arrowRight.style.left = (wrapperRect.right - 32) + 'px';
        arrowRight.style.transform = 'translateY(-50%)';
      }
    }

    function updateArrows() {
      const maxScroll = scrollArea.scrollWidth - scrollArea.clientWidth;

      if (maxScroll <= 2) {
        arrowLeft && arrowLeft.classList.remove('product-compare__arrow--visible');
        arrowRight && arrowRight.classList.remove('product-compare__arrow--visible');
        return;
      }

      if (arrowLeft) {
        arrowLeft.classList.toggle(
          'product-compare__arrow--visible',
          scrollArea.scrollLeft > 5
        );
      }

      if (arrowRight) {
        arrowRight.classList.toggle(
          'product-compare__arrow--visible',
          scrollArea.scrollLeft < maxScroll - 5
        );
      }

      positionArrows();
    }

    function getColumnWidth() {
      const firstCard = section.querySelector('.product-compare__product-card');
      if (!firstCard) return 200;
      const style = window.getComputedStyle(scrollArea.querySelector('.product-compare__row'));
      const gap = parseInt(style.gap, 10) || 16;
      return firstCard.offsetWidth + gap;
    }

    if (arrowLeft) {
      arrowLeft.addEventListener('click', () => {
        scrollArea.scrollBy({ left: -getColumnWidth(), behavior: 'smooth' });
      });
    }

    if (arrowRight) {
      arrowRight.addEventListener('click', () => {
        scrollArea.scrollBy({ left: getColumnWidth(), behavior: 'smooth' });
      });
    }

    /* ================================================================
       6. DOT INDICATORS
       ================================================================ */

    let dotCount = 0;

    function buildDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';

      const scrollWidth = scrollArea.scrollWidth;
      const clientWidth = scrollArea.clientWidth;
      if (scrollWidth <= clientWidth + 2) return;

      const colWidth = getColumnWidth();
      const visibleCols = Math.floor(clientWidth / colWidth) || 1;
      dotCount = Math.ceil(productCount / visibleCols);
      if (dotCount <= 1) return;

      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'product-compare__dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.dataset.page = i;
        dot.addEventListener('click', () => {
          const targetScroll =
            (scrollWidth - clientWidth) * (i / (dotCount - 1));
          scrollArea.scrollTo({ left: targetScroll, behavior: 'smooth' });
        });
        dotsContainer.appendChild(dot);
      }

      updateDots();
    }

    function updateDots() {
      if (!dotsContainer || dotCount <= 1) return;
      const dots = dotsContainer.querySelectorAll('.product-compare__dot');
      if (dots.length === 0) return;

      const maxScroll = scrollArea.scrollWidth - scrollArea.clientWidth;
      if (maxScroll <= 0) return;

      const ratio = scrollArea.scrollLeft / maxScroll;
      const activeIndex = Math.round(ratio * (dotCount - 1));

      dots.forEach((dot, idx) => {
        dot.classList.toggle('product-compare__dot--active', idx === activeIndex);
      });
    }

    /* ================================================================
       7. INIT
       ================================================================ */

    window.addEventListener('scroll', () => {
      updateSticky();
      positionArrows();
      updateArrows();
    }, { passive: true });

    window.addEventListener('resize', () => {
      positionLabels();
      updateSticky();
      updateArrows();
      positionArrows();
      buildDots();
    });

    // Initial setup
    positionLabels();
    updateSticky();
    updateArrows();
    positionArrows();
    buildDots();

    // Recalculate after images load (affects layout heights)
    section.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => {
          positionLabels();
          updateArrows();
          buildDots();
        }, { once: true });
      }
    });

    // Also reposition labels if the Shopify theme editor resizes things
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', positionLabels);
      document.addEventListener('shopify:block:select', positionLabels);
      document.addEventListener('shopify:block:deselect', positionLabels);
    }
  }
})();